import React from 'react';
import { Banknote, CreditCard, Wallet, Landmark, MoreHorizontal } from 'lucide-react';

interface AccountIconProps {
  institutionId?: string;
  type: string;
  className?: string;
  size?: number;
}

export const AccountIcon: React.FC<AccountIconProps> = ({ institutionId, type, className = "", size = 16 }) => {
  // Ueno (Verde lima)
  if (institutionId === 'ueno') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M7 8v6a5 5 0 0 0 10 0V8" />
        <circle cx="17" cy="5" r="1.5" fill="currentColor" stroke="none" className="text-[#c4ff00]" />
      </svg>
    );
  }

  // Itaú (Naranja y Azul oscuro, usamos naranja para el acento)
  if (institutionId === 'itau') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="3" y="3" width="18" height="18" rx="4" />
        <circle cx="12" cy="8" r="1.5" fill="currentColor" stroke="none" className="text-orange-500" />
        <path d="M12 11v6" className="text-orange-500" />
        <path d="M10 17h4" className="text-orange-500" />
        <path d="M10 11h2" className="text-orange-500" />
      </svg>
    );
  }

  // Banco Continental (Azul profundo con logo de globo y triángulo verde)
  if (institutionId === 'continental') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20" />
        <path d="M4 8h16" />
        <path d="M4 16h16" />
        {/* Triángulo central en azul característico */}
        <path d="M12 5l4 8H8z" className="text-blue-600" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  // Banco Familiar (Verde)
  if (institutionId === 'familiar') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="3" y="3" width="18" height="18" rx="4" />
        <path d="M14 7h-2a2 2 0 0 0-2 2v12" className="text-green-500" />
        <path d="M8 13h6" className="text-green-500" />
      </svg>
    );
  }

  // Banco Atlas (Rojo)
  if (institutionId === 'atlas') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M12 3L3 21h18Z" />
        <path d="M8 15h8" className="text-red-600" />
      </svg>
    );
  }

  // Visión Banco (Rojo)
  if (institutionId === 'vision') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M4 4l8 16 8-16" className="text-red-600" />
      </svg>
    );
  }

  // Sudameris (Verde oscuro)
  if (institutionId === 'sudameris') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 12h18" className="text-emerald-600" />
      </svg>
    );
  }

  // Banco Basa (Rojo)
  if (institutionId === 'basa') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M4 4v16h8a4 4 0 0 0 0-8 4 4 0 0 0 0-8H4z" className="text-red-600" />
      </svg>
    );
  }

  // Banco GNB (Teal/Celeste)
  if (institutionId === 'gnb') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2v20" className="text-teal-500" />
      </svg>
    );
  }

  // BNF / Fomento (Dos cuadrados superpuestos en tono dorado/ámbar)
  if (institutionId === 'bnf') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="4" y="4" width="10" height="10" rx="1" />
        <rect x="10" y="10" width="10" height="10" rx="1" className="text-amber-500" />
      </svg>
    );
  }

  // Coomecipar (Letra C con pino de cooperativismo y cruz médica en azul)
  if (institutionId === 'coomecipar') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        {/* Letra C exterior */}
        <path d="M18 7a9 9 0 1 0 0 10" />
        {/* Pino (Cooperativismo) */}
        <path d="M8 14l2-5 2 5" />
        {/* Cruz (Médica) con acento azul */}
        <path d="M15 10v4" className="text-blue-600" />
        <path d="M13 12h4" className="text-blue-600" />
      </svg>
    );
  }

  // Eko (Magenta/Rosa)
  if (institutionId === 'eko') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
        <path d="M8 12h8" className="text-pink-500" />
        <path d="M12 8v8" className="text-pink-500" />
      </svg>
    );
  }

  // Fallbacks by type
  switch (type) {
    case 'EFECTIVO':
      return <Banknote size={size} className={className} />;
    case 'TARJETA DE CREDITO':
      return <CreditCard size={size} className={className} />;
    case 'BANCOS':
      return <Landmark size={size} className={className} />;
    default:
      return <MoreHorizontal size={size} className={className} />;
  }
};
